// JSON-LD structured data for the marketing site.
//
// Keep all schemas centralised so a change to org name / URL / sameAs
// propagates everywhere a page references one of them. Each schema uses
// stable @id values so Google can stitch the graph together (e.g. a
// Service whose provider IS the Organization).
//
// Note: TempDash is positioned to crawlers as an Organization + a technology
// marketplace, EXPLICITLY NOT an EmploymentAgency. The EmploymentAgency @type
// (and an "agency" framing in general) would imply a supply/employment
// relationship that doesn't match how the platform works — and risks the
// agency-legislation / IR35 reading. `disambiguatingDescription` is the
// schema.org property built to clarify exactly this, and AI Overviews read it.
// ⚠️ The legal positioning is solicitor-review-pending (Employment Agencies
// Act 1973 / Simply Learning Tutors v BEIS — see CLAUDE.md §1.2); this copy
// states a genuine marketplace model but isn't a substitute for legal advice.

const SITE_URL = 'https://tempdash.app'
const LOGO_URL = `${SITE_URL}/og/tempdash-logo.png`

const ORG_ID      = `${SITE_URL}/#org`
const WEBSITE_ID  = `${SITE_URL}/#website`

const NOT_AN_AGENCY =
  'TempDash is a technology marketplace and software platform, not an employment ' +
  'agency, employment business or staffing agency. TempDash does not employ, supply ' +
  'or supervise professionals and does not set their pay. Dental practices and ' +
  'self-employed dental professionals contract directly with each other under a ' +
  'Contract for Services; TempDash provides the matching, compliance and payments ' +
  'technology that connects them.'

// ── Reusable nodes ───────────────────────────────────────────────────────────
const orgNode = {
  '@type': 'Organization',
  '@id':   ORG_ID,
  name:    'TempDash',
  legalName: 'TempDash Ltd',
  url:     SITE_URL,
  logo:    LOGO_URL,
  slogan:  'The UK dental locum marketplace',
  description:
    'TempDash is an online marketplace that connects UK dental practices directly ' +
    'with independent, self-employed dental professionals: dentists, orthodontists, ' +
    'hygienists, therapists, dental nurses, treatment coordinators, practice managers ' +
    'and receptionists. Practices set their own rates and engage professionals directly, ' +
    'with no agency markup.',
  // Schema.org's dedicated "clarify what this is / isn't" field.
  disambiguatingDescription: NOT_AN_AGENCY,
  address: {
    '@type': 'PostalAddress',
    streetAddress: '124 City Rd',
    addressLocality: 'London',
    postalCode: 'EC1V 2NX',
    addressCountry: 'GB',
  },
  email: 'support@tempdash.app',
  // The statutory identifiers, matching the footer's s.82 / Article 13 block
  // word for word. A crawler that can tie the site to a real registered company
  // is the cheapest trust signal available, and these are already public.
  identifier: [
    { '@type': 'PropertyValue', name: 'Company number',    value: '17264372' },
    { '@type': 'PropertyValue', name: 'ICO registration',  value: 'ZC159507' },
  ],
  areaServed: { '@type': 'Country', name: 'United Kingdom' },
  knowsAbout: [
    'Dental locum marketplace',
    'Self-employed dental professionals',
    'Contract for Services (not employment)',
    'On-demand dental cover',
    'Right-to-Work compliance',
  ],
  sameAs:  [
    // Add real LinkedIn / X / Instagram URLs when those accounts are live.
  ],
}

// FAQPage builder — the schema MUST mirror visible on-page Q&As (Google
// requires structured-data content to match what users see), so callers pass
// their rendered FAQ list. Used by the /faq page, which carries the explicit
// "Is TempDash an employment agency?" answer for AI Overviews to quote.
export function buildFaqJsonLd(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  }
}

const websiteNode = {
  '@type':   'WebSite',
  alternateName: 'TempDash Dental',
  '@id':     WEBSITE_ID,
  url:       SITE_URL,
  name:      'TempDash',
  publisher: { '@id': ORG_ID },
}

// ── Per-page graphs ──────────────────────────────────────────────────────────

export const landingJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [orgNode, websiteNode],
}

export const forPracticesJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    orgNode,
    {
      '@type':   'Service',
      name:      'Dental practice staffing',
      provider:  { '@id': ORG_ID },
      areaServed: { '@type': 'Country', name: 'United Kingdom' },
      serviceType: 'Direct dental staffing marketplace',
      audience:  { '@type': 'BusinessAudience', audienceType: 'Dental practices' },
      description:
        'Find temporary dentists, orthodontists, hygienists, therapists, dental nurses, ' +
        'treatment coordinators, practice managers and receptionists in minutes. ' +
        'TempDash is the smart matching platform giving UK practices total control over ' +
        'staffing costs with seamless, automated billing.',
    },
  ],
}

export const forWorkersJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    orgNode,
    {
      '@type':   'Service',
      name:      'Fast pay dental shifts',
      provider:  { '@id': ORG_ID },
      areaServed: { '@type': 'Country', name: 'United Kingdom' },
      audience:  {
        '@type': 'Audience',
        audienceType: 'Dental nurses, hygienists, therapists and associate dentists',
      },
      description:
        'Browse dental shifts across the UK, choose what you accept, and get paid directly ' +
        'to your bank within 24 hours of clocking out.',
    },
  ],
}

// /get-paid — the worker/social landing. A richer graph than the plain FAQ
// so the page can rank on intent + role + location terms: Organization +
// a location-scoped Service (same-day pay dental shifts in London) + the
// visible FAQ mirrored as a FAQPage. `faqs` is passed so the structured
// data always matches the on-page Q&A (Google requires the match).
export function buildGetPaidJsonLd(faqs) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      orgNode,
      {
        '@type':    'Service',
        name:       'Same-day pay dental locum shifts',
        provider:   { '@id': ORG_ID },
        serviceType: 'Dental locum marketplace',
        areaServed: [
          { '@type': 'City',    name: 'London' },
          { '@type': 'Country', name: 'United Kingdom' },
        ],
        audience: {
          '@type': 'Audience',
          audienceType:
            'Self-employed dentists, dental hygienists, dental therapists and dental nurses',
        },
        description:
          'Pick up dental locum shifts near you across London and get paid the day you ' +
          'finish, direct to your bank, with no agency. For self-employed dentists, ' +
          'hygienists, therapists and dental nurses.',
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map(({ q, a }) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a },
        })),
      },
    ],
  }
}

// ── The two trunks (2026-10-02 rebuild) ──────────────────────────────────────
// `/` is Find work and `/business` is Get cover (docs/MARKETING-SITE-SPEC.md §3),
// so each trunk home carries its own graph: the Organization, one Service scoped
// to that audience, and the page's VISIBLE FAQ mirrored as a FAQPage.
//
// The faqs argument is not optional by accident. Google requires structured FAQ
// data to match what a visitor can read on the page, so the page passes the same
// array it renders through <FaqAccordion>, and the two cannot disagree.
//
// ⛔ Carried from orgNode above and not to be softened: we are a marketplace, not
// an employment agency. Keep `provider` pointing at ORG_ID and keep the service
// described as matching, compliance and payments technology.

export function findWorkJsonLd(faqs = [], crumbs = null) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      orgNode,
      websiteNode,
      {
        '@type':     'Service',
        name:        'Locum shifts, paid the day you work',
        provider:    { '@id': ORG_ID },
        serviceType: 'Locum marketplace',
        areaServed: [
          { '@type': 'City',    name: 'London' },
          { '@type': 'Country', name: 'United Kingdom' },
        ],
        audience: {
          '@type': 'Audience',
          audienceType:
            'Self-employed dentists, dental hygienists, dental therapists and dental nurses',
        },
        description:
          'Pick up locum shifts near you, see the rate before you accept, and get paid ' +
          'the day you finish. For self-employed professionals who choose their own work.',
      },
      ...(faqs.length ? [{
        '@type': 'FAQPage',
        mainEntity: faqs.map(({ q, a }) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a },
        })),
      }] : []),
      ...(crumbs ? [breadcrumbJsonLd(crumbs)] : []),
    ],
  }
}

// A trail for the Get cover sub pages. Google renders it in place of the raw
// URL in the result, which is worth more on a three level path than it looks.
// `trail` is [[name, path], ...] from the trunk down to this page.
export function breadcrumbJsonLd(trail) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: trail.map(([name, path], i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: `${SITE_URL}${path}`,
    })),
  }
}

export function getCoverJsonLd(faqs = [], crumbs = null) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      orgNode,
      websiteNode,
      {
        '@type':     'Service',
        name:        'Locum cover for your workplace',
        provider:    { '@id': ORG_ID },
        serviceType: 'Locum marketplace',
        areaServed: [
          { '@type': 'City',    name: 'London' },
          { '@type': 'Country', name: 'United Kingdom' },
        ],
        audience: { '@type': 'BusinessAudience', audienceType: 'Dental workplaces' },
        description:
          'Post the shift, see who is coming, and settle it on one bill. Every ' +
          'professional is registered, insured and checked before they can accept.',
      },
      ...(faqs.length ? [{
        '@type': 'FAQPage',
        mainEntity: faqs.map(({ q, a }) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a },
        })),
      }] : []),
      ...(crumbs ? [breadcrumbJsonLd(crumbs)] : []),
    ],
  }
}
